import { clientEnv } from "../env";

export type MemoryCategory = "user" | "project";
export type MemoryConsentCategory = MemoryCategory | "conversation" | "document";

export interface MemoryRecord {
  readonly id: string;
  readonly userId: string;
  readonly category: MemoryCategory;
  readonly content: string;
  readonly source?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

interface ApiErrorPayload {
  readonly error?: string;
  readonly message?: string;
}

export class MemoryClientError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "MemoryClientError";
    this.status = status;
  }
}

export async function getMemoryConsent(sessionToken: string): Promise<MemoryConsentCategory[]> {
  const payload = await memoryJson<{ categories?: string[] }>("/memory/consent", sessionToken);
  return (payload.categories ?? []).filter(
    (category): category is MemoryConsentCategory => ["user", "project", "conversation", "document"].includes(category)
  );
}

export async function setMemoryConsent(
  sessionToken: string,
  enabled: boolean
): Promise<MemoryConsentCategory[]> {
  const payload = await memoryJson<{ categories?: string[] }>("/memory/consent", sessionToken, {
    method: "PUT",
    body: { categories: enabled ? ["user", "project", "conversation"] : [] }
  });
  return (payload.categories ?? []).filter(
    (category): category is MemoryConsentCategory => ["user", "project", "conversation", "document"].includes(category)
  );
}

export async function setConversationContinuity(sessionToken: string, enabled: boolean): Promise<MemoryConsentCategory[]> {
  const current = await getMemoryConsent(sessionToken);
  const categories: MemoryConsentCategory[] = current.filter(category => category !== "conversation");
  if (enabled) categories.push("conversation");
  const payload = await memoryJson<{ categories: MemoryConsentCategory[] }>("/memory/consent", sessionToken, {
    method: "PUT", body: { categories }
  });
  return payload.categories;
}

export async function listMemories(sessionToken: string): Promise<MemoryRecord[]> {
  const payload = await memoryJson<{ memories?: MemoryRecord[] }>("/memory", sessionToken);
  return payload.memories ?? [];
}

export async function saveMemory(
  sessionToken: string,
  input: { category: MemoryCategory; content: string; source?: string }
): Promise<MemoryRecord> {
  return memoryJson<MemoryRecord>("/memory", sessionToken, {
    method: "POST",
    body: input
  });
}

export async function updateMemory(
  sessionToken: string,
  memoryId: string,
  content: string
): Promise<MemoryRecord> {
  const payload = await memoryJson<{ memory: MemoryRecord }>(
    `/memory/${encodeURIComponent(memoryId)}`,
    sessionToken,
    { method: "PUT", body: { content } }
  );
  return payload.memory;
}

export async function deleteMemory(sessionToken: string, memoryId: string): Promise<void> {
  await memoryJson(`/memory/${encodeURIComponent(memoryId)}`, sessionToken, {
    method: "DELETE"
  });
}

export async function deleteAllMemories(sessionToken: string): Promise<number> {
  const payload = await memoryJson<{ deleted?: number }>("/memory/all", sessionToken, {
    method: "DELETE"
  });
  return payload.deleted ?? 0;
}

async function memoryJson<T = { ok: boolean }>(
  path: string,
  sessionToken: string,
  options: { method?: "GET" | "POST" | "PUT" | "DELETE"; body?: unknown } = {}
): Promise<T> {
  const response = await fetch(`${clientEnv.apiBaseUrl}${path}`, {
    method: options.method ?? "GET",
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      ...(options.body === undefined ? {} : { "Content-Type": "application/json" })
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body)
  });
  const payload = await readJson<T | ApiErrorPayload>(response);
  if (!response.ok) {
    const error = payload as ApiErrorPayload;
    throw new MemoryClientError(
      response.status,
      error.message ?? error.error ?? "No se pudo completar la operación de memoria."
    );
  }
  return payload as T;
}

async function readJson<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T;
  } catch {
    throw new MemoryClientError(response.status, "La respuesta de memoria no es válida.");
  }
}
